import * as React from "react";
import {
    Button,
    CircularProgress,
    TextField,
    Typography,
    Chip,
    Box,
    RadioGroup,
    FormControlLabel,
    Radio
} from "@material-ui/core";
import { useSnackbar } from "notistack";
import AssignmentIndIcon from "@material-ui/icons/AssignmentInd";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { apiPost, apiGet } from "../services/api";
import ModalDialog, { useModalDialog } from "./ModalDialog";
import IUser from "../models/IUser";

interface IAddCustomerProps {
    onSuccess: () => void;
    projectId: number;
    projectName: string;
}

type AddMethod = "user" | "domain";

function UsersAutoCompleteAdd(props: {
    options: IUser[];
    value: IUser[];
    onChange: (values: IUser[]) => void;
    loading: boolean;
}) {
    const [open, setOpen] = React.useState(false);
    const [inputValue, setInputValue] = React.useState("");

    return (
        <Autocomplete
            multiple
            disableCloseOnSelect
            filterSelectedOptions
            renderTags={() => null}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            options={props.options.filter(
                u => !props.value.some(v => v.id === u.id)
            )}
            getOptionLabel={u => `${u.firstName} ${u.lastName} [${u.email}]`}
            value={props.value}
            inputValue={inputValue}
            onInputChange={(_, v) => setInputValue(v)}
            onChange={(_, v) => {
                props.onChange(v);
                setInputValue(inputValue);
            }}
            loading={props.loading}
            renderInput={params => (
                <TextField
                    {...params}
                    label="Select users"
                    variant="outlined"
                    fullWidth
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {props.loading && (
                                    <CircularProgress size={20} />
                                )}
                                {params.InputProps.endAdornment}
                            </>
                        )
                    }}
                />
            )}
        />
    );
}

const BulkAddCustomers: React.FC<IAddCustomerProps> = ({
    projectId,
    projectName,
    onSuccess
}) => {
    const [modalOpen, , toggleModal] = useModalDialog();
    const [addMethod, setAddMethod] = React.useState<AddMethod>("user");
    const [selectedUsers, setSelectedUsers] = React.useState<IUser[]>([]);
    const [allUsers, setAllUsers] = React.useState<IUser[]>([]);
    const [domainInput, setDomainInput] = React.useState("");
    const [loading, setLoading] = React.useState(false);
    const [loadingMessage, setLoadingMessage] = React.useState<string | null>(
        null
    );
    const [confirmStep, setConfirmStep] = React.useState(false);
    const snackbar = useSnackbar();

    const updateUserList = (users: IUser[]) => {
        setAllUsers(users ?? []);
    }

    const startLoading = (message: string | null = null) => {
        setLoadingMessage(message);
        setLoading(true);
    };

    const stopLoading = () => {
        setLoading(false);
        setLoadingMessage(null);
    };

    React.useEffect(() => {
        if (!modalOpen) return;
        const getAllUsers = async () => {
            setLoading(true);
            const url: string = `users/all`;
            try {
                const response = await apiGet(url);
                updateUserList(response as IUser[]);
            } catch {
                updateUserList([]);
            } finally {
                setLoading(false);
            }
        };
        getAllUsers();
    }, [modalOpen]);

    const sortedSelected = React.useMemo(() => {
        return [...selectedUsers].sort(
            (a, b) => 
                (a.firstName ?? a.email).localeCompare(b.firstName ?? b.email) ||
                (a.lastName ?? a.email).localeCompare(b.firstName ?? b.email)
        );
    }, [selectedUsers]);

    const handleAddDomain = () => {
        const trimmedDomainInput = domainInput
            .trim()
            .replace(/^@/, "")
            .toLowerCase();
        if (!trimmedDomainInput) return;
        setDomainInput("");
        const toAdd = allUsers.filter(
            u =>
                u.email.toLowerCase().endsWith(`@${trimmedDomainInput}`) &&
                !selectedUsers.some(s => s.id === u.id)
        );
        if (!toAdd.length) {
            snackbar.enqueueSnackbar(`No users for @${trimmedDomainInput}`, {
                variant: "warning"
            });
            return;
        }
        setSelectedUsers(prev => [...prev, ...toAdd]);
    };

    const handleConfirm = () => {
        if (!confirmStep) {
            setConfirmStep(true);
            return;
        }
        startLoading(
            `Inviting ${selectedUsers.length} ${
                selectedUsers.length > 1 ? "users" : "user"
            }...`
        );
        const payload = selectedUsers.map(u => ({
            projectIds: [projectId],
            userId: u.id,
            email: u.email,
            firstName: u.firstName,
            lastName: u.lastName,
            phoneNumber: (u as any).phoneNumber || ""
        }));
        apiPost("users/inviteMany", payload)
            .then((added: string[]) => {
                if (added.length > 5) {
                    snackbar.enqueueSnackbar(`${added.length} users invited`, {
                        variant: "success"
                    });
                } else {
                    snackbar.enqueueSnackbar(`Invited: ${added.join(", ")}`, {
                        variant: "success"
                    });
                }
                setSelectedUsers([]);
                toggleModal();
                onSuccess();
            })
            .catch(() => {
                snackbar.enqueueSnackbar("Error sending invitations", {
                    variant: "error"
                });
            })
            .finally(() => {
                stopLoading();
                setConfirmStep(false);
            });
    };

    const handleClose = () => {
        toggleModal();
        setConfirmStep(false);
        setSelectedUsers([]);
        setDomainInput("");
        stopLoading();
    };

    return (
        <>
            <Button
                variant="outlined"
                color="primary"
                onClick={toggleModal}
                startIcon={<AssignmentIndIcon />}
            >
                Bulk Add Customers
            </Button>
            <ModalDialog
                isActive={modalOpen}
                title={`Bulk Add Customers to ${projectName}`}
                confirmDisabled={!sortedSelected.length}
                confirmAction={handleConfirm}
                confirmButtonText={confirmStep ? "Send Invitations" : "Review"}
                handleClose={handleClose}
                preventClose={loading}
                showError={false}
            >
                {loading ? (
                    <Box
                        minHeight={80}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <CircularProgress />
                        {loadingMessage && (
                            <Typography
                                variant="body1"
                                style={{ marginTop: 8 }}
                            >
                                {loadingMessage}
                            </Typography>
                        )}
                    </Box>
                ) : confirmStep ? (
                    <Box>
                        <Typography gutterBottom>
                            {`The following ${sortedSelected.length} user${
                                sortedSelected.length > 1 ? "s" : ""
                            } will receive an invitation:`}
                        </Typography>
                        <Box mb={2}>
                            {sortedSelected.map(u => (
                                <Typography key={u.id}>{u.email}</Typography>
                            ))}
                        </Box>
                        <Typography>Do you want to continue?</Typography>
                    </Box>
                ) : (
                    <Box>
                        <RadioGroup
                            row
                            value={addMethod}
                            onChange={(_, v) => setAddMethod(v as AddMethod)}
                        >
                            <FormControlLabel
                                value="user"
                                control={<Radio />}
                                label="By user"
                            />
                            <FormControlLabel
                                value="domain"
                                control={<Radio />}
                                label="By domain"
                            />
                        </RadioGroup>
                        {addMethod === "user" ? (
                            <UsersAutoCompleteAdd
                                options={allUsers}
                                value={selectedUsers}
                                onChange={setSelectedUsers}
                                loading={loading}
                            />
                        ) : (
                            <Box display="flex" alignItems="center">
                                <TextField
                                    label="Email domain"
                                    variant="outlined"
                                    value={domainInput}
                                    onChange={e =>
                                        setDomainInput(e.target.value)
                                    }
                                    fullWidth
                                />
                                <Box ml={1}>
                                    <Button
                                        onClick={handleAddDomain}
                                        variant="contained"
                                        color="primary"
                                    >
                                        Add
                                    </Button>
                                </Box>
                            </Box>
                        )}
                        <Box mt={2}>
                            {sortedSelected.map(u => (
                                <Chip
                                    key={u.id}
                                    label={u.email}
                                    onDelete={() =>
                                        setSelectedUsers(prev =>
                                            prev.filter(p => p.id !== u.id)
                                        )
                                    }
                                    style={{ margin: 4 }}
                                />
                            ))}
                        </Box>
                    </Box>
                )}
            </ModalDialog>
        </>
    );
};

export default BulkAddCustomers;
